/**
 * Movie Studio Content tab. Idea, lines, and the picture button.
 * Role names are not shown here.
 */

import {
  ABOUT_MOVING,
  ABOUT_STILL,
  STILL_HINT,
} from '@/lib/reel/reelCopy';
import type { ContentPanelState } from '@/lib/reel/contentPanelState';

interface ContentPanelProps {
  state: ContentPanelState;
  onIdea: (idea: string) => void;
  onLine: (index: number, line: string) => void;
  onWrite: () => void;
  onMakePictures: () => void;
  onUsePhotos: () => void;
  onToggleAdjust: () => void;
  onMovePictures: (move: boolean) => void;
}

/** Left column of Movie Studio. Libraries stay on the next tab. */
export default function ContentPanel({
  state,
  onIdea,
  onLine,
  onWrite,
  onMakePictures,
  onUsePhotos,
  onToggleAdjust,
  onMovePictures,
}: ContentPanelProps): JSX.Element {
  const moving = state.movePictures || state.treatment.picture === 'moving';

  return (
    <div className="vt-content">
      <label className="vt-content-label" htmlFor="vt-content-idea">
        What is this about?
      </label>
      <textarea
        id="vt-content-idea"
        className="vt-content-idea"
        placeholder="A spiral galaxy in deep space"
        value={state.idea}
        rows={4}
        onChange={(event) => onIdea(event.target.value)}
      />
      <button
        type="button"
        className="vt-content-primary"
        onClick={onWrite}
        disabled={state.busy}
      >
        Write
      </button>
      {state.status ? (
        <p className="vt-content-status" role="status">
          {state.status}
        </p>
      ) : null}
      <p className="vt-content-label">Captions</p>
      <ol className="vt-content-lines">
        {state.treatment.beats.map((beat) => (
          <li key={beat.index}>
            <span className="vt-content-num">{beat.index}</span>
            <input
              className="vt-content-line"
              aria-label={`Caption ${beat.index}`}
              placeholder="Type, then drag it on the picture"
              value={beat.line}
              onChange={(event) => onLine(beat.index, event.target.value)}
            />
          </li>
        ))}
      </ol>
      <button
        type="button"
        className="vt-content-primary"
        onClick={onMakePictures}
        disabled={state.busy}
      >
        Make pictures
      </button>
      <p className="vt-content-hint">
        {moving ? ABOUT_MOVING : `${ABOUT_STILL} · ${STILL_HINT}`}
      </p>
      <button type="button" className="vt-content-link" onClick={onUsePhotos}>
        Use my photos
      </button>
      <button
        type="button"
        className="vt-content-link"
        aria-expanded={state.adjustOpen}
        onClick={onToggleAdjust}
      >
        Adjust the pictures
      </button>
      {state.adjustOpen ? (
        <label className="vt-content-adjust">
          <input
            type="checkbox"
            checked={state.movePictures}
            onChange={(event) => onMovePictures(event.target.checked)}
          />
          Let the picture move
        </label>
      ) : null}
    </div>
  );
}
